import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  FlightScope,
  FlightStatus,
  FlightType,
  ScheduleCriteria,
  SortableField,
} from '@/types/flight';
import { toApiDateTime } from '@/lib/api/date';
import { defaultStation, stationWithin } from '@/types/flightAccess';
import { FLIGHT_SCOPES, FLIGHT_STATUSES, FLIGHT_TYPES, isSortableField } from '@/types/flight';

/**
 * The table's state, held in the URL and nowhere else.
 *
 * THE URL IS THE STATE. It is not mirrored into React state, and no effect copies
 * one into the other. v1's dashboard kept `filters` in state and synced it to the
 * query string with two effects; each rebuilt a fresh object the other treated as a
 * change, and a Back navigation made them feed each other until React aborted the
 * render with "Maximum update depth exceeded".
 *
 * Here the flow is one-way: the URL is read on render, and a user action writes the
 * URL. A refresh, a shared link and the Back button then all work for free, because
 * there is no second copy that could disagree.
 *
 * Writes use `replace`, so typing in a filter does not bury the page the reader
 * arrived from under one history entry per keystroke.
 */

/** Short names, because this query string is meant to be read and pasted. */
const KEYS = {
  airport: 'airport',
  type: 'type',
  status: 'status',
  flight: 'flight',
  airline: 'airline',
  from: 'from',
  to: 'to',
  scope: 'scope',
  stand: 'stand',
  /**
   * A named period — `today`, `last7d`. Mutually exclusive with the two dates
   * below: one of them is in the URL, never both.
   */
  range: 'range',
  dateFrom: 'dateFrom',
  dateTo: 'dateTo',
  page: 'page',
  rows: 'rows',
  sort: 'sort',
  dir: 'dir',
  /** The flight whose panel is open. */
  id: 'id',
} as const;

export const ROWS_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_ROWS = 50;

/** A board is read by scheduled time, ascending — the order it happens in. */
const DEFAULT_SORT: SortableField = 'scheduledDate';

export interface ScheduleParams {
  /** Text filters, matched as `LIKE %value%` server-side. */
  airport: string;
  flight: string;
  airline: string;
  stand: string;
  /**
   * The multi-selects: any of these, matched EXACTLY. In the URL as one
   * comma-separated value — `from=ALA,NQZ` — so a link stays readable, and an old
   * link holding a single code is simply a list of one.
   *
   * `from` is the ROUTE's origin, not a date; `to` its destination.
   */
  from: string[];
  to: string[];
  status: FlightStatus[];
  type: FlightType | '';
  scope: FlightScope | '';
  /**
   * The period, when it is a named one.
   *
   * `null` means the two dates below were set by hand. THE NAME IS WHAT IS STORED,
   * not the instants it works out to, for two reasons. A control that says "Last 7
   * days" has to know that is what it is showing, and deriving it back from a pair of
   * timestamps is a guess that fails the moment a rolling window is a minute old. And
   * a board is a live thing: a link that says `range=last7d` should mean the last
   * seven days when it is opened, not the seven days it meant when it was sent.
   *
   * The instants are resolved from it once per URL, so nothing downstream has to know
   * the difference — and resolving per render would change `criteria` every render,
   * which is how a request loop starts.
   */
  range: RangePreset | null;
  /** Local wall clock, `yyyy-MM-ddTHH:mm`, inclusive both ends. Always resolved. */
  dateFrom: string;
  dateTo: string;
  page: number;
  rows: number;
  sort: SortableField;
  dir: 'ascending' | 'descending';
  /**
   * The row whose detail panel is open, or null.
   *
   * In the URL like everything else, so a refresh keeps the panel open and a
   * colleague can be sent the exact flight rather than "the third one down".
   */
  id: number | null;
}

const isOneOf = <T extends string>(options: readonly T[], value: string | null): value is T =>
  value !== null && (options as readonly string[]).includes(value);

/**
 * A comma-separated URL value as a list: trimmed, upper-cased, blanks and repeats
 * dropped. Upper case because both lists hold codes — IATA codes and status names.
 */
const listOf = (raw: string | null): string[] => [
  ...new Set(
    (raw ?? '')
      .split(',')
      .map((item) => item.trim().toUpperCase())
      .filter(Boolean),
  ),
];

/** The statuses in their canonical order, whatever order the URL or the clicks gave. */
const statusesOf = (raw: string | null): FlightStatus[] => {
  const asked = new Set(listOf(raw));
  return FLIGHT_STATUSES.filter((status) => asked.has(status));
};

/** `2026-08-19T00:00` — the local wall clock, which is what the reader typed. */
const localInput = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export interface DateRange {
  dateFrom: string;
  dateTo: string;
}

export const dayRange = (day: Date): DateRange => {
  const start = new Date(day);
  start.setHours(0, 0, 0, 0);

  const end = new Date(day);
  end.setHours(23, 59, 0, 0);

  return { dateFrom: localInput(start), dateTo: localInput(end) };
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

/**
 * The quick ranges the period picker offers.
 *
 * The NAME goes in the URL and the instants are worked out from it per URL, so
 * `range=last7d` still means the last seven days tomorrow. Doing it the other way —
 * freezing the instants at the moment of the click — read fine in a shared link and
 * made the control unable to say what it was showing, because a pair of timestamps
 * cannot be matched back to "last 24 hours" once a minute has passed.
 *
 * Resolving them per URL rather than per render matters: `new Date()` in a render
 * would change `criteria` every time, which is how a request loop starts.
 */
export const RANGE_PRESETS = [
  'today',
  'yesterday',
  'last24h',
  'last7d',
  'last30d',
  'thisMonth',
] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number];

/** What the board shows when nothing has been asked for. */
export const DEFAULT_RANGE: RangePreset = 'today';

export const isRangePreset = (value: string | null): value is RangePreset =>
  value !== null && (RANGE_PRESETS as readonly string[]).includes(value);

export const presetRange = (preset: RangePreset, now: Date = new Date()): DateRange => {
  switch (preset) {
    case 'today':
      return dayRange(now);
    case 'yesterday':
      return dayRange(addDays(now, -1));
    // The one preset that is not day-aligned, because a rolling window ending now is
    // what "in the last 24 hours" means to the person asking for it.
    case 'last24h':
      return {
        dateFrom: localInput(new Date(now.getTime() - 24 * 60 * 60 * 1000)),
        dateTo: localInput(now),
      };
    case 'last7d':
      return { dateFrom: dayRange(addDays(now, -6)).dateFrom, dateTo: dayRange(now).dateTo };
    case 'last30d':
      return { dateFrom: dayRange(addDays(now, -29)).dateFrom, dateTo: dayRange(now).dateTo };
    case 'thisMonth': {
      // Day 0 of the next month is the last day of this one, leap years included.
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { dateFrom: dayRange(first).dateFrom, dateTo: dayRange(last).dateTo };
    }
  }
};

/**
 * The same window, moved by its own length.
 *
 * A day steps a day and a week steps a week: on a board the arrows mean "the period
 * before this one", not "24 hours earlier than whatever I am looking at".
 */
export const shiftRange = (range: DateRange, direction: -1 | 1): DateRange => {
  const from = new Date(range.dateFrom);
  const to = new Date(range.dateTo);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return range;

  // A whole day is 23:59 long, so this rounds up: one day steps one, seven step seven.
  const days = Math.max(1, Math.ceil((to.getTime() - from.getTime()) / 86_400_000));

  return {
    dateFrom: localInput(addDays(from, days * direction)),
    dateTo: localInput(addDays(to, days * direction)),
  };
};

/**
 * Whether a range covers whole days — 00:00 to 23:59 — rather than a rolling window.
 *
 * It decides how the range is written down: whole days are named by their dates,
 * because 00:00 and 23:59 tell a reader nothing, while a window that starts at 14:20
 * has to show that or it is a lie.
 */
export const isDayAligned = ({ dateFrom, dateTo }: DateRange): boolean => {
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;

  return (
    from.getHours() === 0 && from.getMinutes() === 0 && to.getHours() === 23 && to.getMinutes() === 59
  );
};

/**
 * Whether every row on this board falls on today.
 *
 * **The one case where a bare time needs no date beside it.** A reader looking at
 * today can fill the date in themselves; on any other day they cannot, and `14:30`
 * with nothing next to it silently reads as this afternoon.
 *
 * This replaced a "does the range cross midnight" test, which got a single past day
 * wrong: pick Yesterday and every row showed a bare time, on a board that was not
 * about today at all. Crossing midnight is a special case of not-being-today, so
 * one question covers both.
 *
 * Read during render rather than resolved once, which is safe here because it feeds
 * a label and not `criteria` — a `new Date()` in the path that builds the request is
 * how a fetch loop starts. A tab left open past midnight keeps the old answer until
 * something re-renders, and then correctly starts dating a board that has become
 * yesterday's.
 */
export const coversOnlyToday = ({ dateFrom, dateTo }: DateRange): boolean => {
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;

  const today = new Date().toDateString();
  return from.toDateString() === today && to.toDateString() === today;
};

/** Whether a range is exactly one calendar day. */
export const isWholeDay = (range: DateRange): boolean =>
  isDayAligned(range) && new Date(range.dateFrom).toDateString() === new Date(range.dateTo).toDateString();

/**
 * @param stationsOnly The stations this reader may pick — their access's airports — or
 *   `null` for every station. Inside an access the station is held to these, and a
 *   reader with exactly one opens on it; see `stationWithin` in types/flightAccess.ts.
 *   Pass a stable array: it is a dependency of `params`.
 */
export const useScheduleParams = (stationsOnly: readonly string[] | null = null) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<ScheduleParams>(() => {
    const text = (key: string) => searchParams.get(key)?.trim() ?? '';

    const rows = Number(searchParams.get(KEYS.rows));
    const page = Number(searchParams.get(KEYS.page));
    const sort = searchParams.get(KEYS.sort);
    const id = Number(searchParams.get(KEYS.id));
    const dir = searchParams.get(KEYS.dir);

    // The period, resolved once per URL. Dates set by hand win; then a named period;
    // then today, because the API has no row cap and "no date filter" means the whole
    // table — a very expensive answer to a question nobody asked.
    const custom = searchParams.get(KEYS.dateFrom) ?? searchParams.get(KEYS.dateTo);
    const named = searchParams.get(KEYS.range);
    const preset: RangePreset | null =
      custom !== null ? null : isRangePreset(named) ? named : DEFAULT_RANGE;
    const period = preset === null
      ? {
          dateFrom: searchParams.get(KEYS.dateFrom) ?? '',
          dateTo: searchParams.get(KEYS.dateTo) ?? '',
        }
      : presetRange(preset);

    return {
      airport: stationWithin(text(KEYS.airport), stationsOnly),
      flight: text(KEYS.flight),
      airline: text(KEYS.airline),
      stand: text(KEYS.stand),
      from: listOf(searchParams.get(KEYS.from)),
      to: listOf(searchParams.get(KEYS.to)),
      status: statusesOf(searchParams.get(KEYS.status)),
      type: isOneOf(FLIGHT_TYPES, searchParams.get(KEYS.type)) ? searchParams.get(KEYS.type) as FlightType : '',
      scope: isOneOf(FLIGHT_SCOPES, searchParams.get(KEYS.scope))
        ? (searchParams.get(KEYS.scope) as FlightScope)
        : '',
      range: preset,
      dateFrom: period.dateFrom,
      dateTo: period.dateTo,
      // A page or size that is not a positive number falls back rather than being
      // sent: `rows: 0` in particular returns no rows beside a real total count.
      page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1,
      rows: Number.isFinite(rows) && rows >= 1 ? Math.floor(rows) : DEFAULT_ROWS,
      sort: isSortableField(sort) ? sort : DEFAULT_SORT,
      dir: dir === 'descending' ? 'descending' : 'ascending',
      id: Number.isInteger(id) && id > 0 ? id : null,
    };
  }, [searchParams, stationsOnly]);

  /**
   * Applies a change.
   *
   * Any change other than the page itself resets to page 1: a narrower filter
   * otherwise leaves the reader on a page that no longer exists, and this API
   * quietly answers with the LAST page instead of an empty one — which reads as the
   * filter having been ignored.
   */
  const update = useCallback(
    (changes: Partial<ScheduleParams>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          for (const [key, value] of Object.entries(changes)) {
            if (value === '' || value === undefined || value === null) {
              next.delete(key);
            } else if (Array.isArray(value)) {
              // An emptied multi-select leaves the URL, rather than as `from=`.
              if (value.length === 0) next.delete(key);
              else next.set(key, value.join(','));
            } else {
              next.set(key, String(value));
            }
          }

          // A filter change starts the pager over, but opening or closing a
          // flight is not a filter change: it must leave the page alone.
          const onlySelection = Object.keys(changes).every((key) => key === 'id');
          if (!('page' in changes) && !onlySelection) next.delete(KEYS.page);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const reset = useCallback(() => setSearchParams(new URLSearchParams(), { replace: true }), [
    setSearchParams,
  ]);

  /**
   * Whether the range was ASKED for, as opposed to being today by default.
   *
   * The params above cannot say: they carry today's range either way, which is what
   * makes them simple to read. But a clear button for a filter nobody set would be a
   * control that does nothing, so the difference is reported here.
   */
  const hasExplicitRange = useMemo(
    () =>
      searchParams.has(KEYS.dateFrom) || searchParams.has(KEYS.dateTo) || searchParams.has(KEYS.range),
    [searchParams],
  );

  /**
   * Whether anything is narrowing the table — what "Clear all" is for.
   *
   * The station counts only when it is not the one the board opens on: a reader with a
   * single airport always has it, and a clear button that cannot clear it would be a
   * control that does nothing.
   */
  const hasFilters = useMemo(
    () =>
      hasExplicitRange ||
      params.airport !== defaultStation(stationsOnly) ||
      [params.from, params.to, params.status].some((list) => list.length > 0) ||
      [params.flight, params.airline, params.stand, params.type, params.scope].some(
        (value) => value !== '',
      ),
    [params, hasExplicitRange, stationsOnly],
  );

  /** The request body for these params. `id` is a UI concern and is not sent. */
  const criteria = useMemo<ScheduleCriteria>(
    () => ({
      airportIataCode: params.airport,
      flightNumber: params.flight,
      airlineIataCode: params.airline,
      // The array forms only. A single field sent beside its array is a SECOND
      // condition, not a fallback, so the singles are never sent.
      ...(params.from.length > 0 ? { departureAirportIataCodes: params.from } : {}),
      ...(params.to.length > 0 ? { arrivalAirportIataCodes: params.to } : {}),
      ...(params.status.length > 0 ? { flightStatuses: params.status } : {}),
      // Filtered as `planeParking`; the same column sorts as `planeParkingName`.
      planeParking: params.stand,
      ...(params.type ? { flightType: params.type } : {}),
      ...(params.scope ? { flightScope: params.scope } : {}),
      // The wall clock the reader typed, sent unchanged. This backend parses a
      // zoneless `yyyy-MM-dd HH:mm:ss` in its OWN timezone, so converting to UTC
      // first moved the day: a request for 19 August 00:00 left as 18 August
      // 19:00Z. See lib/api/date.ts — the API doc's claim that ISO-8601 is
      // accepted here is wrong.
      ...(params.dateFrom ? { scheduledDateStart: toApiDateTime(params.dateFrom) } : {}),
      ...(params.dateTo ? { scheduledDateEnd: toApiDateTime(params.dateTo) } : {}),
      pageNum: params.page,
      rows: params.rows,
      sortBy: params.sort,
      sortDirection: params.dir,
    }),
    [params],
  );

  return { params, criteria, update, reset, hasExplicitRange, hasFilters };
};
